import React from 'react'
import styled from 'styled-components'
import InfoPopover, { PopItem, PopList } from 'features/actions/workspace/InfoPopover'
import { TASK_ORIGINS } from 'features/taskManagement/taskOrigin'

const OPTIONS = [{ value: null, label: 'All types', color: null, description: 'Show every kind of task' }, ...TASK_ORIGINS]

const TaskOriginPicker = ({
  value,
  onChange,
  label = 'Task type',
  compact = false,
  options = OPTIONS,
  infoTitle = 'What do the task types mean?',
  infoItems = TASK_ORIGINS,
  infoNote = 'A task keeps its type for its whole life, even if it is edited later.'
}) => {
  return (
    <Group role='group' aria-label={`Filter by ${label.toLowerCase()}`} $compact={compact}>
      <GroupLabel>{label}</GroupLabel>
      {options.map(opt => {
        const on = (value || null) === opt.value
        return (
          <Chip
            key={opt.value || 'all'}
            type='button'
            title={opt.description}
            aria-pressed={on}
            $active={on}
            $color={opt.color}
            onClick={() => onChange(opt.value)}
          >
            {opt.color && <ChipDot $color={opt.color} $active={on} />}
            {opt.label}
          </Chip>
        )
      })}
      <InfoPopover label={infoTitle} title={infoTitle}>
        <PopList>
          {infoItems.map(o => (
            <PopItem key={o.value}>
              {o.color && <PopDot $color={o.color} />}
              <div>
                <strong>{o.label}</strong>
                <br />
                {o.description}.
              </div>
            </PopItem>
          ))}
        </PopList>
        {infoNote && <InfoNote>{infoNote}</InfoNote>}
      </InfoPopover>
    </Group>
  )
}

export default TaskOriginPicker

const Group = styled.div`
  display: inline-flex; align-items: center; flex-wrap: wrap; gap: 4px;
  padding: 3px; border-radius: 22px;
  background: ${({ theme }) => theme.colors?.backgroundColor || '#f3f4f6'};
  border: 1px solid ${({ theme }) => theme.colors?.lightGrey || '#e5e7eb'};

  @media (max-width: 768px) { border-radius: 14px; }
`

const GroupLabel = styled.span`
  font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: #6b7280; padding: 0 4px 0 8px;
`

const Chip = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  min-height: 34px; padding: 6px 13px; border-radius: 18px;
  border: 1px solid ${({ $active, $color }) => ($active ? ($color || '#0C1229') : '#e5e7eb')};
  background: ${({ $active, $color }) => ($active ? ($color || '#0C1229') : '#fff')};
  color: ${({ $active }) => ($active ? '#fff' : '#0C1229')};
  font: inherit; font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;

  &:hover { border-color: ${({ $color }) => $color || '#0C1229'}; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }

  @media (max-width: 768px) { min-height: 40px; padding: 8px 14px; font-size: 14px; }
`

const ChipDot = styled.span`
  width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; margin-top: 1px;
  background: ${({ $active, $color }) => ($active ? '#fff' : $color)};
`

const PopDot = styled.span`
  flex: 0 0 auto; width: 10px; height: 10px; margin-top: 6px; border-radius: 50%;
  background: ${({ $color }) => $color};
`

const InfoNote = styled.div`
  margin-top: 12px; padding-top: 10px; border-top: 1px solid #f0f1f5;
  font-size: 13px; line-height: 1.45; color: #667085;
`
